package com.heelion.farmafreelance.models

import kotlinx.serialization.Serializable
import kotlinx.datetime.Instant

@Serializable
enum class AnnuncioTipo {
    OFFRO_LAVORO, // Usato dalle farmacie
    CERCO_LAVORO  // Usato dai farmacisti
}

@Serializable
enum class AnnuncioStato {
    APERTO, CHIUSO, CANCELLATO
}

@Serializable
enum class MatchStato {
    PROPOSTO, ACCETTATO, RIFIUTATO, CONFERMATO, COMPLETATO, ANNULLATO
}

@Serializable
enum class TariffaTipo {
    DIURNO, NOTTURNO, FESTIVO_DIURNO, FESTIVO_NOTTURNO
}

@Serializable
enum class CreatoreTipo {
    FARMACIA, FARMACISTA, INFERMIERE
}

/** Professioni che lavorano nelle farmacie: ognuna ha le sue tabelle di match, storico e avvisi di pagamento. */
@Serializable
enum class Professione {
    FARMACISTA, INFERMIERE
}

/**
 * Quello che farmacisti e infermieri hanno in comune: anagrafica, fisco, zona di lavoro e
 * reputazione. Schede, elenchi e ricerche possono lavorare su un professionista qualsiasi;
 * i dati di ciascuno restano comunque nella sua tabella.
 */
interface Professionista {
    val id: String?
    val nome: String
    val cognome: String
    val partitaIva: String
    val codiceFiscale: String?
    val sesso: String?
    val dataNascita: String?
    val luogoNascita: String?
    val cittadinanza: String?
    val titoloStudio: String?
    val email: String
    val telefono: String?
    val regimeFiscale: String?
    val aliquotaForfettario: Double
    val regioneOperativita: String?
    val provinciaOperativita: String?
    val comuneOperativita: String?
    val capOperativita: String?
    val provinceOperativita: List<String>
    val comuniOperativita: List<String>
    val disponibileTrasferta: Boolean
    val lat: Double?
    val lng: Double?
    val descrizione: String?
    val competenze: List<String>
    val storicoCompletati: Int
    val storicoAnnullati: Int
    val tutorialVisto: Boolean
    /** Solo all'iscrizione: poi si cambia con /api/account/consensi. */
    val consensoMarketing: Boolean

    val nomeCompleto: String get() = "$nome $cognome"
}

@Serializable
data class Farmacista(
    override val id: String? = null,
    val userId: String? = null,
    override val nome: String,
    override val cognome: String,
    override val partitaIva: String,
    override val codiceFiscale: String? = null,
    val numeroIscrizioneOrdine: String? = null,
    override val sesso: String? = null,
    override val dataNascita: String? = null,
    override val luogoNascita: String? = null,
    override val cittadinanza: String? = null,
    override val titoloStudio: String? = null,
    override val email: String,
    val password: String? = null,
    override val telefono: String? = null,
    override val regimeFiscale: String? = null,    // FORFETTARIO, ORDINARIO, ecc.
    val percEnpaf: Double = 2.0,          // Default 2%
    override val aliquotaForfettario: Double = 15.0, // 15%, oppure 5% nei primi 5 anni (solo regime forfettario)
    override val regioneOperativita: String? = null,
    override val provinciaOperativita: String? = null,
    override val comuneOperativita: String? = null,
    override val capOperativita: String? = null,
    override val provinceOperativita: List<String> = emptyList(), // Lista di sigle province (es: ["MI", "BG"])
    override val comuniOperativita: List<String> = emptyList(),  // Lista di nomi comuni (es: ["Milano", "Monza"])
    override val disponibileTrasferta: Boolean = false,
    override val lat: Double? = null,
    override val lng: Double? = null,
    override val descrizione: String? = null,
    override val competenze: List<String> = emptyList(),
    override val storicoCompletati: Int = 0,
    override val storicoAnnullati: Int = 0,
    /** false solo per i nuovi iscritti finché non vedono (o saltano) il tour di Farmy. */
    override val tutorialVisto: Boolean = true,
    override val consensoMarketing: Boolean = false,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
) : Professionista

@Serializable
data class Infermiere(
    override val id: String? = null,
    override val nome: String,
    override val cognome: String,
    override val partitaIva: String,
    override val codiceFiscale: String? = null,
    val numeroIscrizioneOpi: String? = null,
    override val sesso: String? = null,
    override val dataNascita: String? = null,
    override val luogoNascita: String? = null,
    override val cittadinanza: String? = null,
    override val titoloStudio: String? = null,
    override val email: String,
    val password: String? = null,
    override val telefono: String? = null,
    override val regimeFiscale: String? = null,
    val percEnpapi: Double = 4.0,           // Contributo integrativo ENPAPI in fattura
    override val aliquotaForfettario: Double = 15.0,
    override val regioneOperativita: String? = null,
    override val provinciaOperativita: String? = null,
    override val comuneOperativita: String? = null,
    override val capOperativita: String? = null,
    override val provinceOperativita: List<String> = emptyList(),
    override val comuniOperativita: List<String> = emptyList(),
    override val disponibileTrasferta: Boolean = false,
    override val lat: Double? = null,
    override val lng: Double? = null,
    override val descrizione: String? = null,
    override val competenze: List<String> = emptyList(),
    val polizzaRcNumero: String? = null,
    val polizzaRcScadenza: String? = null,   // yyyy-MM-dd
    override val storicoCompletati: Int = 0,
    override val storicoAnnullati: Int = 0,
    override val tutorialVisto: Boolean = true,
    override val consensoMarketing: Boolean = false,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
) : Professionista

@Serializable
data class Farmacia(
    val id: String? = null,
    val userId: String? = null,
    val nomeFarmacia: String,
    val ragioneSociale: String? = null, // Per fatturazione
    val partitaIva: String,
    val codiceFiscale: String? = null,
    val codiceSDI: String? = null,      // Per fatturazione
    val pec: String? = null,            // Per fatturazione
    val indirizzo: String,
    val cap: String? = null,
    val citta: String? = null,
    val provincia: String? = null,
    val regione: String? = null,
    val stato: String = "Italia",
    val lat: Double? = null,
    val lng: Double? = null,
    val email: String,
    val password: String? = null,
    val telefono: String,
    val numeroIscrizioneOrdineDirettore: String? = null,
    val tipo: String? = null,
    val descrizione: String? = null,
    val storicoCompletati: Int = 0,
    val storicoAnnullati: Int = 0,
    /** false solo per i nuovi iscritti finché non vedono (o saltano) il tour di Farmy. */
    val tutorialVisto: Boolean = true,
    /**
     * La farmacia lavora anche con gli infermieri (prelievi, iniezioni, medicazioni...): solo allora
     * l'app le mostra la parte infermieri. null = mai scelto (le app vecchie non lo mandano e il
     * server lo lascia com'è).
     */
    val serviziInfermieristici: Boolean? = null,
    /** Solo all'iscrizione: poi si cambia con /api/account/consensi. */
    val consensoMarketing: Boolean = false,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class AnnuncioTurno(
    val id: String? = null,
    val creatoDa: CreatoreTipo,
    val creatoreId: String,
    val tipo: AnnuncioTipo,
    val titolo: String,
    val descrizione: String? = null,
    val giornoSettimana: String? = null, // Aggiunto per gestire Lunedì, Martedì, ecc.
    val dataTurno: Instant,
    val dataTurnoFine: Instant? = null,
    val oraInizio: String,
    val oraFine: String,
    val zonaTestuale: String? = null,
    val lat: Double? = null,
    val lng: Double? = null,
    val compenso: Double? = null,
    val tariffaTipo: TariffaTipo = TariffaTipo.DIURNO,
    val stato: AnnuncioStato = AnnuncioStato.APERTO,
    val quantitaMassima: Int = 1,  // Numero di farmacisti richiesti
    val postiOccupati: Int = 0,    // Numero di farmacisti che hanno confermato
    /** Chi cerca la farmacia con questo turno; per le disponibilità è la professione di chi le pubblica. */
    val professioneRichiesta: Professione = Professione.FARMACISTA,
    val nomeCreatore: String? = null, // Aggiunto per visualizzazione rapida
    val localitaCreatore: String? = null, // Stile Immobiliare: via/città/cap
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class Match(
    val id: String? = null,
    val annuncioId: String,
    /** Id del professionista: farmacista o infermiere, vedi [professione]. */
    val farmacistaId: String,
    val farmaciaId: String,
    val stato: MatchStato = MatchStato.PROPOSTO,
    val messaggio: String? = null,
    val propostoDa: CreatoreTipo,
    val dataTurno: Instant? = null, // Aggiunto per ordinamento e grouping
    val nomeControparte: String? = null, // Aggiunto per visualizzare il nome nella Home
    val partitaIvaControparte: String? = null, // Aggiunto per visualizzare la P.IVA
    val regioneControparte: String? = null,    // Filtro geografico
    val provinciaControparte: String? = null,  // Filtro geografico
    val indirizzoControparte: String? = null,  // Per la UI stile immobiliare
    val cittaControparte: String? = null,     // Per la UI stile immobiliare
    val capControparte: String? = null,       // Per la UI stile immobiliare
    /** Gli id dei match degli infermieri hanno il prefisso "inf-": si rimandano così come sono. */
    val professione: Professione = Professione.FARMACISTA,
    val createdAt: Instant? = null,
    val updatedAt: Instant? = null
)

@Serializable
data class UserReputation(
    val completedCount: Int = 0,
    val cancelledCount: Int = 0,
    val confirmedCount: Int = 0,
    val stars: Double = 0.0,
    val discountEligible: Boolean = false,
    val nextDiscountProgress: Int = 0, // Turni mancanti allo sconto
    val hasEnoughData: Boolean = false
)

@Serializable
data class Notification(
    val id: String? = null,
    val utenteId: String,
    val senderId: String? = null,
    val titolo: String,
    val messaggio: String,
    val tipo: String, // INFO, CONFIRM, CANCEL
    val data: Instant,
    val isRead: Boolean = false,
    /** Proposta a cui si riferisce (id di [Match]): dalla notifica si può accettare, confermare... */
    val matchId: String? = null
)

/**
 * Dispositivo registrato alle notifiche push (Firebase Cloud Messaging): il server manda le
 * notifiche di [utenteId] (es. "FARMACISTA_12", stesso id di [Notification.utenteId]) a [token].
 */
@Serializable
data class DispositivoPush(
    val utenteId: String,
    val token: String,
    val piattaforma: String, // ANDROID, WEB
    /** Tipi di notifica (Notification.tipo, es. "CANCEL") che questo dispositivo non vuole in push. */
    val tipiDisattivati: List<String> = emptyList()
)

@Serializable
data class Competenza(
    val id: String? = null,
    val nome: String,
    val descrizione: String? = null,
    val categoria: String? = null
)

@Serializable
data class FarmacistaCompetenza(
    val id: String? = null,
    val farmacistaId: String,
    val competenzaId: String,
    val livello: String? = null,
    val dataConseguimento: Instant? = null
)

@Serializable
data class BatchEntry(
    val dataTurno: Instant,
    val oraInizio: String,
    val oraFine: String,
    val compenso: Double? = null
)

@Serializable
data class BatchAnnuncioRequest(
    val template: AnnuncioTurno,
    val entries: List<BatchEntry>
)

@Serializable
data class LoginRequest(
    val email: String,
    val password: String,
    val role: UserRole? = null
)

@Serializable
data class LoginResponse(
    val success: Boolean,
    val role: UserRole? = null,
    val email: String? = null,
    val message: String? = null,
    /** Token di sessione: l'app lo manda in ogni richiesta (header Authorization). */
    val token: String? = null,
    /** Password giusta ma è attiva la verifica in due passaggi: serve il codice dell'app di autenticazione. */
    val richiedeCodice: Boolean = false,
    /** Da rimandare insieme al codice (vale 5 minuti, non è una sessione). */
    val tokenTemporaneo: String? = null
)

/** Secondo passaggio del login: codice a 6 cifre dell'app di autenticazione, o un codice di emergenza. */
@Serializable
data class CodiceDueFattoriRequest(val tokenTemporaneo: String, val codice: String)

@Serializable
data class DueFattoriStato(val attiva: Boolean, val codiciEmergenzaRimasti: Int = 0)

/** Attivazione: [segreto] da scrivere nell'app di autenticazione, oppure [uri] da aprire/inquadrare. */
@Serializable
data class DueFattoriAvvio(val segreto: String, val uri: String)

@Serializable
data class DueFattoriCodice(val codice: String)

/** Codici di emergenza: si vedono solo ora, poi nel server resta solo la loro impronta. */
@Serializable
data class DueFattoriCodiciEmergenza(val codici: List<String>)

@Serializable
data class DueFattoriDisattiva(val password: String, val codice: String)

/** Consensi facoltativi dell'utente, oggi solo le email promozionali. */
@Serializable
data class ConsensiAccount(val marketing: Boolean)

/** "Elimina account": la password conferma che è proprio il titolare a chiederlo. */
@Serializable
data class EliminaAccountRequest(val password: String)

@Serializable
data class PasswordResetRequest(val email: String)

@Serializable
data class PasswordResetConfirmRequest(val email: String, val code: String, val newPassword: String)

@Serializable
data class PasswordResetResponse(val success: Boolean, val message: String? = null)

/** "Non ricordi l'email?": codice fiscale o partita IVA con cui ci si è iscritti. */
@Serializable
data class RecuperoEmailRequest(val codice: String)

/** Email trovate, mascherate (es. f***********1@gmail.com): si riconoscono ma non si leggono. */
@Serializable
data class RecuperoEmailResponse(val emails: List<String> = emptyList(), val message: String? = null)

/**
 * "Ho perso l'accesso" (né password né email): richiesta che l'amministratore verifica a mano.
 * Se approvata, l'email dell'account diventa [nuovaEmail] e lì arriva il codice per la password.
 */
@Serializable
data class RecuperoAccessoRequest(
    val codice: String,
    val numeroIscrizioneOrdine: String? = null,
    val telefono: String,
    val nuovaEmail: String,
    val messaggio: String = ""
)

@Serializable
data class StoricoEntry(
    val id: String? = null,
    val annuncioId: String,
    val farmacistaId: String,
    val farmaciaId: String,
    val dataTurno: Instant,
    val oraInizio: String,
    val oraFine: String,
    val compenso: Double,
    // Campi opzionali per la UI (popolati con join se serve)
    val nomeFarmacia: String? = null,
    val nomeFarmacista: String? = null,
    /** Solo per il farmacista (sua agenda dei pagamenti): quando è stato pagato; null = da incassare. */
    val pagatoIl: Instant? = null,
    /** Per gli infermieri [farmacistaId] è l'id dell'infermiere e [nomeFarmacista] il suo nome. */
    val professione: Professione = Professione.FARMACISTA
)

@Serializable
data class FatturaEntry(
    val id: String? = null,
    val numeroFattura: String,
    val annuncioIds: List<String>,
    val farmacistaId: String,
    val farmaciaId: String,
    val dataEmissione: Instant,
    val importoTotale: Double,
    val nomeFile: String,
    val nomeFarmacia: String? = null,
    val dataTurno: Instant? = null,
    val orario: String? = null,
    val professione: Professione = Professione.FARMACISTA
)

@Serializable
enum class AppThemeColor(val hex: String) {
    WHITE("0xFFFFFFFF"),
    GREEN("0xFF00A388"),
    BLUE("0xFF2196F3"),
    RED("0xFFE53935"),
    YELLOW("0xFFFFB300"),
    PINK("0xFFE91E63"),
    PURPLE("0xFF9C27B0"),
    ORANGE("0xFFFB8C00")
}

@Serializable
enum class SubscriptionType {
    FREE, TRIAL, MONTHLY, YEARLY
}

@Serializable
data class UserStatus(
    val userId: String,
    val isPremium: Boolean,
    val subscriptionType: SubscriptionType = SubscriptionType.FREE,
    val userRole: UserRole? = null,
    val subscribedAt: Long? = null,         // Data di sottoscrizione (Timestamp)
    val expiryDate: Long? = null,           // Data di scadenza (Timestamp)
    val daysRemaining: Int? = null
) {
    val isTrial: Boolean get() = subscriptionType == SubscriptionType.TRIAL
    
    // Controlla se l'abbonamento è ancora valido temporalmente
    val isValidPremium: Boolean get() {
        if (!isPremium) return false
        val expiry = expiryDate ?: return true // Se premium ma senza scadenza (es. admin), è valido
        return expiry > kotlinx.datetime.Clock.System.now().toEpochMilliseconds()
    }
}
